import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCurrentUser, trackAnalyticsEvent } from '@/lib/api';
import { validateEventData, getSchemaVersion, sanitizeEventData } from '@/lib/analytics/schemas';

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Detect product ID from current route
const getProductIdFromRoute = (pathname: string, userPrimaryProduct?: string | null): string | null => {
  // Admin routes - track as 'admin'
  if (pathname.startsWith('/admin')) {
    return 'admin';
  }

  // Product-specific routes
  if (pathname.includes('/products/mindcotine') || pathname.includes('/mindcotine')) {
    return 'mindcotine';
  }
  // Check for mindco-relief paths
  if (pathname.includes('/products/mindco-relief') || pathname.includes('/mindco-relief')) {
    return 'mindco-relief';
  }

  // Shared routes - use user's primary product (which will be 'mindcotine' or 'mindco-relief')
  return userPrimaryProduct || null;
};

interface AnalyticsEvent {
  event_type: 'page_view' | 'button_click' | 'feature_usage' | 'video_engagement' | 'article_engagement' | 'audio_engagement' | 'user_journey';
  event_name: string;
  event_data?: Record<string, any>;
}

export const useAnalytics = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const previousPathRef = useRef<string>('');

  // Track page view
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    
    // Skip if it's the same page
    if (previousPathRef.current === currentPath) {
      return;
    }

    previousPathRef.current = currentPath;

    trackEvent({
      event_type: 'page_view',
      event_name: t('common.useanalytics.page_view', 'Page View'),
      event_data: {
        page_path: location.pathname,
        path: location.pathname,
        search: location.search,
        hash: location.hash,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, t]);

  // Track event function with schema validation
  const trackEvent = useCallback(async (event: AnalyticsEvent) => {
    try {
      // Validate event data against schema
      const eventData = event.event_data || {};
      const { valid, errors } = validateEventData(event.event_type, eventData);
      
      if (!valid) {
        console.warn(`[Analytics] Skipping invalid event ${event.event_type}:`, errors);
        return; // Don't track invalid events
      }

      // Sanitize and add schema version
      const sanitizedData = sanitizeEventData(event.event_type, eventData);
      const schemaVersion = getSchemaVersion(event.event_type);

      // Add timeout to prevent analytics from blocking user interactions
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Analytics timeout')), 5000)
      );

      const trackingPromise = (async () => {
        const userResult = await getCurrentUser();
        const userId = userResult.success ? userResult.data.id : null;
        // primary_product_id is on profiles table, not auth.users - rely on route-based detection
        const userPrimaryProduct = null;

        // Detect product from current route and user preference
        const product_id = getProductIdFromRoute(location.pathname, userPrimaryProduct);

        await trackAnalyticsEvent(userId, getSessionId(), {
          event_type: event.event_type,
          event_name: event.event_name,
          event_data: { ...sanitizedData, _schema_version: schemaVersion },
          page_path: location.pathname,
          referrer: document.referrer,
          user_agent: navigator.userAgent,
          device_type: getDeviceType(),
          product_id: product_id || undefined, // Include product context in all events
        });
      })();

      // Race between tracking and timeout - whichever finishes first
      await Promise.race([trackingPromise, timeoutPromise]);
    } catch (error) {
      // Silently fail - analytics should never block user interactions
      console.debug('Analytics tracking error:', error);
    }
  }, [location]);

  // Track button click
  const trackButtonClick = useCallback((buttonName: string, additionalData?: Record<string, any>) => {
    trackEvent({
      event_type: 'button_click',
      event_name: buttonName,
      event_data: {
        button_name: buttonName,
        ...additionalData
      },
    });
  }, [trackEvent]);

  // Track feature usage
  const trackFeatureUsage = useCallback((featureName: string, additionalData?: Record<string, any>) => {
    trackEvent({
      event_type: 'feature_usage',
      event_name: featureName,
      event_data: {
        feature_name: featureName,
        ...additionalData
      },
    });
  }, [trackEvent]);

  // Track video engagement
  const trackVideoEngagement = useCallback((action: string, videoData: Record<string, any>) => {
    trackEvent({
      event_type: 'video_engagement',
      event_name: `Video ${action}`,
      event_data: {
        action,
        ...videoData
      },
    });
  }, [trackEvent]);

  // Track article engagement
  const trackArticleEngagement = useCallback((action: string, articleData: Record<string, any>) => {
    trackEvent({
      event_type: 'article_engagement',
      event_name: `Article ${action}`,
      event_data: {
        action,
        ...articleData
      },
    });
  }, [trackEvent]);

  // Track audio engagement
  const trackAudioEngagement = useCallback((action: string, audioData: Record<string, any>) => {
    trackEvent({
      event_type: 'audio_engagement',
      event_name: `Audio ${action}`,
      event_data: {
        action,
        ...audioData
      },
    });
  }, [trackEvent]);

  // Track user journey
  const trackUserJourney = useCallback((journeyStep: string, additionalData?: Record<string, any>) => {
    trackEvent({
      event_type: 'user_journey',
      event_name: journeyStep,
      event_data: {
        step_name: journeyStep,
        ...additionalData
      },
    });
  }, [trackEvent]);

  return {
    trackButtonClick,
    trackFeatureUsage,
    trackVideoEngagement,
    trackArticleEngagement,
    trackAudioEngagement,
    trackUserJourney,
    trackEvent,
  };
};
